import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

const LanguageContext = createContext();

// Create RTL cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache
const cacheLtr = createCache({
  key: 'muiltr',
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, baseTheme }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      setIsRTL(lng === 'ar');
      document.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };

    // Set initial direction
    handleLanguageChange(i18n.language);

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const switchLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  // Create theme with RTL support
  const theme = React.useMemo(() => {
    return createTheme({
      ...baseTheme,
      direction: isRTL ? 'rtl' : 'ltr',
      typography: {
        ...baseTheme.typography,
        fontFamily: isRTL 
          ? '"Cairo", "Tajawal", "Arial", sans-serif'
          : '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
    });
  }, [baseTheme, isRTL]);

  const contextValue = {
    currentLanguage,
    isRTL,
    switchLanguage,
    getLocalizedField: (obj, fieldName) => {
      if (!obj) return '';
      const suffix = currentLanguage === 'ar' ? '_ar' : '_en';
      return obj[`${fieldName}${suffix}`] || obj[fieldName] || '';
    },
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      <CacheProvider value={isRTL ? cacheRtl : cacheLtr}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </CacheProvider>
    </LanguageContext.Provider>
  );
};
